/**
 * Стандартні звіти (ч.1036): ОСВ → ОСВ по рахунку → картка рахунку → документ.
 *
 * Тут — чиста модель без React: стан у адресі (org/from/to + свій для
 * кожного звіту), посилання ланцюжка, дерево «згорнути/розгорнути»,
 * підсумки по місяцях для картки, формат чисел. Усе перевіряється тестами
 * без браузера.
 */
export interface LedgerPeriod { org: string; from: string; to: string }

export const isIsoDate = (s: string | null | undefined): s is string =>
  !!s && /^\d{4}-\d{2}-\d{2}$/.test(s);

/** Період за замовчуванням: з шапки; «усі дати» у шапці → поточний рік. */
export function defaultPeriod(headerFrom?: string, headerTo?: string, today = new Date()): { from: string; to: string } {
  const y = today.getFullYear();
  const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return {
    from: isIsoDate(headerFrom?.slice(0, 10)) ? headerFrom!.slice(0, 10) : `${y}-01-01`,
    to: isIsoDate(headerTo?.slice(0, 10)) ? headerTo!.slice(0, 10) : iso(today),
  };
}

function qs(params: Record<string, string | undefined | null>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) p.set(k, v);
  return p.toString();
}

/** Посилання ланцюжка — єдине місце, де знаються адреси сторінок. */
export const ledgerLinks = {
  trialBalance: (p: LedgerPeriod & { expanded?: boolean }) =>
    `/reports/trial-balance?${qs({ org: p.org, from: p.from, to: p.to, expanded: p.expanded ? '1' : undefined })}`,
  trialBalanceAccount: (p: LedgerPeriod & { account: string; group?: string }) =>
    `/reports/trial-balance/account?${qs({ org: p.org, account: p.account, from: p.from, to: p.to, group: p.group })}`,
  /** Картка: `account` порожній + `sc` — картка субконто (усі рахунки); `side` — лише Дт/Кт. */
  accountCard: (p: LedgerPeriod & { account?: string | null; sc?: string | null; scName?: string | null;
    corr?: string | null; side?: 'DT' | 'CR' | null }) =>
    `/reports/account-card?${qs({ org: p.org, account: p.account, from: p.from, to: p.to, sc: p.sc, scName: p.scName, corr: p.corr, side: p.side })}`,
  accountAnalysis: (p: LedgerPeriod & { account: string }) =>
    `/reports/account-analysis?${qs({ org: p.org, account: p.account, from: p.from, to: p.to })}`,
  accountTurnover: (p: LedgerPeriod & { account: string; per?: string }) =>
    `/reports/account-turnover?${qs({ org: p.org, account: p.account, from: p.from, to: p.to, per: p.per })}`,
  generalLedger: (p: LedgerPeriod & { per?: string }) =>
    `/reports/general-ledger?${qs({ org: p.org, from: p.from, to: p.to, per: p.per })}`,
  chessSheet: (p: LedgerPeriod & { level?: string }) =>
    `/reports/chess-sheet?${qs({ org: p.org, from: p.from, to: p.to, level: p.level })}`,
  subcontoAnalysis: (p: LedgerPeriod & { type?: string; id?: string | null; name?: string | null }) =>
    `/reports/subconto-analysis?${qs({ org: p.org, type: p.type, id: p.id, name: p.name, from: p.from, to: p.to })}`,
  print: (kind: 'tb' | 'tba' | 'card' | 'sa' | 'an' | 'to' | 'gl' | 'chess', params: Record<string, string | undefined | null>) =>
    `/reports/ledger-print?${qs({ kind, ...params })}`,
};

/** Дерево: рядок видимий, якщо жоден предок не згорнутий. Рядки йдуть у
 *  порядку обходу дерева з полем `level`. */
export function visibleRows<T extends { level: number }>(rows: T[], collapsed: Set<string>, keyOf: (r: T) => string): T[] {
  const out: T[] = [];
  let hiddenBelow: number | null = null;   // рівень згорнутого предка
  for (const r of rows) {
    if (hiddenBelow !== null && r.level > hiddenBelow) continue;
    hiddenBelow = null;
    out.push(r);
    if (collapsed.has(keyOf(r))) hiddenBelow = r.level;
  }
  return out;
}

export function toggleCollapsed(set: Set<string>, key: string): Set<string> {
  const next = new Set(set);
  if (next.has(key)) next.delete(key); else next.add(key);
  return next;
}

/** Картка: підсумки по місяцях — рядки-роздільники з оборотами місяця і
 *  сальдо на кінець місяця (останнє наростаюче сальдо у місяці). */
export interface CardLine {
  entry_date: string; amount_dt: string; amount_cr: string; running_balance: string;
}
export interface MonthSubtotal {
  month: string;            // YYYY-MM
  turnover_dt: number; turnover_cr: number; closing: number; count: number;
}
export function monthSubtotals<T extends CardLine>(rows: T[]): MonthSubtotal[] {
  const by = new Map<string, MonthSubtotal>();
  for (const r of rows) {
    const m = r.entry_date.slice(0, 7);
    const s = by.get(m) ?? { month: m, turnover_dt: 0, turnover_cr: 0, closing: 0, count: 0 };
    s.turnover_dt += Number(r.amount_dt);
    s.turnover_cr += Number(r.amount_cr);
    s.closing = Number(r.running_balance);
    s.count += 1;
    by.set(m, s);
  }
  return [...by.values()];
}

export const PERIODICITY = [
  { value: 'month', label: 'місяць' }, { value: 'quarter', label: 'квартал' }, { value: 'year', label: 'рік' },
];

export const MONTH_UK = ['січень', 'лютий', 'березень', 'квітень', 'травень', 'червень',
  'липень', 'серпень', 'вересень', 'жовтень', 'листопад', 'грудень'];
export function monthLabel(ym: string): string {
  const [y, m] = ym.split('-');
  return `${MONTH_UK[Number(m) - 1] ?? m} ${y}`;
}

/** Гроші: порожньо замість «0,00» — так читається ОСВ (1С теж не малює нулі). */
export function money(v: string | number | null | undefined, { zero = '' }: { zero?: string } = {}): string {
  const n = Number(v ?? 0);
  if (!n) return zero;
  return n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Кількість: без зайвих нулів (до 3 знаків), нуль — порожньо. */
export function qty(v: string | number | null | undefined): string {
  const n = Number(v ?? 0);
  if (!n) return '';
  return n.toLocaleString('uk-UA', { maximumFractionDigits: 3 });
}

/** Сальдо зі знаком для картки: додатне — Дт, від'ємне — Кт. */
export function signedBalance(v: string | number): { text: string; side: 'dt' | 'cr' | 'zero' } {
  const n = Number(v);
  if (!n) return { text: '0,00', side: 'zero' };
  return { text: money(Math.abs(n)), side: n > 0 ? 'dt' : 'cr' };
}

/** Позабалансові — окремим блоком у кінці (у плані рахунків клас 0 йде першим). */
export function splitOffBalance<T extends { is_off_balance: boolean }>(rows: T[]): { balance: T[]; off: T[] } {
  return { balance: rows.filter((r) => !r.is_off_balance), off: rows.filter((r) => r.is_off_balance) };
}
